import React, {Fragment} from 'react';
import {List} from 'immutable';
import {range} from 'lodash';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {
  type ContextType,
  Atom,
  Plugins,
  withMeta,
  withSystem,
  AtomRenderProps,
  Expressions,
} from '@packages/systems/wfdl';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {getIn, setIn} from '@packages/utilities/imm4-backports';
import {
  CLASS_NAME_DYNAMIC_LIST,
  CLASS_NAME_DYNAMIC_LIST_ROW,
} from './constants';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {getProductCollectionId} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {convertRepeaterSlugPath} from '@packages/systems/dynamo/utils/SlugUtils';
import {
  ExpressionElement,
  TListOfElement,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useTemplate} from '@packages/systems/render';
import {
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';
import {isDynamoItem} from '@packages/utilities/elementTypeUtils';

const createTemplateId = (elementId: string) => `wf-template-${elementId}`;

const memo100 = cacheMemo(100);
// @ts-expect-error - TS7006 - Parameter 'oldContext' implicitly has an 'any' type.
const updateRepeaterItemContextMemoFactory = weakMemo((oldContext) =>
  weakMemo((convertedRepeaterSlugPath: string[]) =>
    // @ts-expect-error - TS7006 - Parameter 'repeaterItemIndex' implicitly has an 'any' type.
    memo100((repeaterItemIndex) => {
      const getRepeaterItemContext = (
        templateRendering: boolean
      ): ContextType => {
        const itemId = oldContext.get('itemId', null);

        return (
          oldContext
            // @ts-expect-error - TS7006 - Parameter 'mutableContext' implicitly has an 'any' type.
            .withMutations((mutableContext) =>
              mutableContext
                .set('contextItemId', itemId)
                .remove('__collectionId')
                .remove('itemSlug')
                .set('liveTemplateRendering', templateRendering)
            )
            // @ts-expect-error - TS7006 - Parameter 'pageItem' implicitly has an 'any' type.
            .updateIn(['cachedItems', itemId], (pageItem) => {
              if (pageItem && convertedRepeaterSlugPath) {
                const valueAtIndex = getIn(
                  pageItem,
                  convertedRepeaterSlugPath.concat(repeaterItemIndex)
                );

                return setIn(pageItem, convertedRepeaterSlugPath, valueAtIndex);
              }
              return pageItem;
            })
        );
      };

      let templateRenderingContext: ContextType | undefined;
      let liveRenderingContext: ContextType | undefined;

      return (templateRendering: boolean): ContextType => {
        if (templateRendering) {
          return (templateRenderingContext ??= getRepeaterItemContext(true));
        }

        return (liveRenderingContext ??= getRepeaterItemContext(false));
      };
    })
  )
);

const renderListItemContent = ({
  context,
  convertedRepeaterSlugPath,
  dynamoItemChild,
  renderChild,
  templateRendering,
}: {
  context: ContextType;
  convertedRepeaterSlugPath: string[];
  dynamoItemChild: unknown;
  renderChild: AtomRenderProps['renderChild'];
  templateRendering: boolean;
}) => {
  const getRepeaterItemContext = updateRepeaterItemContextMemoFactory(context)(
    convertedRepeaterSlugPath
  );

  // We use templateRendering to identify if we are rendering the template content, in which case we need at least
  // one item in the case the the default item (for now this only applies for variants) does not have any repeater items
  // and the empty state is displayed; so for this context we will have at least one item that we can use for generating more items
  // for those items that do has repeater items.
  return range(
    templateRendering ? 1 : context.get('repeaterItemsCount', 0)
  ).map((repeaterItemIndex) => {
    const newContext =
      getRepeaterItemContext(repeaterItemIndex)(templateRendering);

    return renderChild(
      dynamoItemChild,
      newContext,
      String(repeaterItemIndex),
      repeaterItemIndex
    );
  });
};

const DynamoList = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    renderChild,
    context,
    data,
    elementId,
  }) => {
    const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
    const listItemIds = context.get('listItemIds', List());

    const children = getChildren(data);
    let dynamoItemChild: unknown = null;
    let hasFullWidthItem = false;

    for (const child of children) {
      if (
        React.isValidElement(child) &&
        child.props &&
        typeof child.props === 'object' &&
        'element' in child.props
      ) {
        const childElement = child.props.element as ExpressionElement;
        if (isDynamoItem(childElement)) {
          dynamoItemChild = child;
          if (
            Expressions.getElementDataAt(
              childElement,
              [
                {in: 'Record', at: 'dyn'},
                {in: 'Record', at: 'grid'},
              ],
              'Number',
              12
            ) === 12
          ) {
            hasFullWidthItem = true;
          }
          break;
        }
      }
    }

    const repeaterSlugPath = context.get('repeaterSlugPath');
    const convertedRepeaterSlugPath =
      repeaterSlugPath && convertRepeaterSlugPath(repeaterSlugPath);

    let listClasses = classes.push(CLASS_NAME_DYNAMIC_LIST);
    if (!hasFullWidthItem) {
      listClasses = listClasses.push(CLASS_NAME_DYNAMIC_LIST_ROW);
    }

    const listAttributes = attributes.set('role', 'list');
    const template = useTemplate();

    if (listItemIds.size > 0) {
      return (
        <Tag {...generateAttributes(listAttributes, listClasses)}>
          {/* @ts-expect-error - TS7006 - Parameter 'itemId' implicitly has an 'any' type. | TS7006 - Parameter 'itemIndex' implicitly has an 'any' type. */}
          {listItemIds.map((itemId, itemIndex) => {
            const newContext = getPluginContext(context)(itemId);
            return renderChild(dynamoItemChild, newContext, itemId, itemIndex, {
              collectionId: newContext.get('collectionId'),
              itemId,
              type: 'cms-list',
            });
          })}
        </Tag>
      );
    } else if (
      convertedRepeaterSlugPath &&
      convertedRepeaterSlugPath.length > 0
    ) {
      const repeaterItemsCount = context.get('repeaterItemsCount');
      const collections = context.get('collections');
      const productsCollectionId =
        collections && getProductCollectionId(collections);
      const currentCollectionIsProductsCollection =
        context.get('collectionId') === productsCollectionId;
      const updatedListClasses =
        repeaterItemsCount && repeaterItemsCount > 0
          ? listClasses
          : listClasses.concat('w-dyn-hide');
      const tagAttributes = currentCollectionIsProductsCollection
        ? {
            ...generateAttributes(listAttributes, updatedListClasses),
            [WF_COLLECTION_DATA_KEY]: convertedRepeaterSlugPath
              .join('.')
              .replace(/^f_default_sku_7dr\./, ''),
            [WF_TEMPLATE_ID_DATA_KEY]: createTemplateId(elementId),
          }
        : {...generateAttributes(listAttributes, updatedListClasses)};

      const templateContent = template(
        renderListItemContent({
          context,
          convertedRepeaterSlugPath,
          dynamoItemChild,
          renderChild,
          templateRendering: true,
        })
      );
      return (
        <Fragment>
          <script type={WF_TEMPLATE_TYPE} id={createTemplateId(elementId)}>
            {templateContent}
          </script>
          <Tag {...tagAttributes}>
            {renderListItemContent({
              context,
              convertedRepeaterSlugPath,
              dynamoItemChild,
              renderChild,
              templateRendering: false,
            })}
          </Tag>
        </Fragment>
      );
    } else {
      return null;
    }
  }
);

const getPluginContext = weakMemo((oldContext: ContextType) =>
  memo100((itemId: string) =>
    oldContext
      .set('contextItemId', oldContext.get('itemId', null))
      // __collectionId is a hack to pass list collection id from DynamoWrapper
      // down to DynamoList without affecting current bindable collection.
      .set('collectionId', oldContext.get('__collectionId'))
      // contextCollectionId is needed for validating Conditional Visibility that uses `DYN_CONTEXT.itemRef`.
      .set('contextCollectionId', oldContext.get('collectionId', null))
      .remove('__collectionId')
      .set('itemId', itemId)
      .remove('listItemIds')
      .remove('itemSlug')
  )
);

const config = createIntegratedAtom({
  displayName: 'Collection List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: true,
  uiStateOnSelected: 'items',
  constraints: {
    children: [
      {
        is: 'DynamoItem',
        name: 'Element',
        rule: 'RequireOnly',
        messages: {
          paste:
            'Paste this element inside the Collection Item element to add it to each Collection item.',
          move: 'Move this element inside the Collection Item element to add it to each Collection item.',
        },
      },
    ],
    pinToParent: true,
  },
  render: DynamoList,
  fields: {
    tag: withMeta(
      {variant: 'menu'},
      withSystem(
        {
          defaultValue: 'div',
          label: 'Tag',
          private: true,
        },
        TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
      )
    ),
    children: TListOfElement,
  },
});

export default config;
